import React from "react";
import {
	View,
	Platform,
	Dimensions,
} from "react-native";

import Style from "ae-style";
import StyledText from "ae-base/Base/StyledText";
import ResponsiveStyleSheet from "react-native-responsive-stylesheet";
import {
	VictoryLine,
	VictoryBar,
	VictoryChart,
	VictoryAxis,
	VictoryLabel,
	VictoryContainer,
} from "victory-native";

import { useAtaTextSize, useResources } from "@aetonix/hooks";

const { width, height } = Dimensions.get("window");

var styles = ResponsiveStyleSheet.createSized("min-direction", {
	0: {
		container: {
			alignSelf: "center",
		},
		textY: {
			paddingTop: 20,
			marginLeft: width < 450 ? 20 : 0,
		},
		textX: {
			paddingBottom: 20,
		},
		viewForScrolling: {
			width: "100%",
			height: "100%",
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			zIndex: 1,
		},
	},
	512: {
		container: {
			width: 530,
			height: 300,
			marginBottom: 80,
		},
	},
});

const responsiveStyles = ResponsiveStyleSheet.createOriented({
	landscape: {
		container: {
			alignSelf: "center",
			paddingLeft: 30,
		},
	},
	portrait: {
		container: {
			alignSelf: "center",
		},
	},
});

function isFiniteNumber(value) {
	if (value === null || value === undefined) return false;
	var n = typeof value === "number" ? value : Number(value);
	return typeof n === "number" && isFinite(n);
}

function isValidPoint(item, xKey, yKey) {
	if (!item) return false;
	var x = xKey ? item[xKey] : item.x;
	var y = yKey ? item[yKey] : item.y;
	if (x === null || x === undefined || x === "") return false;
	if (!isFiniteNumber(y)) return false;
	if (item.y0 !== undefined && item.y0 !== null && !isFiniteNumber(item.y0)) return false;
	return true;
}

function VictoryGraph(props) {
	const textSize = useAtaTextSize();
	var rawData = Array.isArray(props.data) ? props.data : [];
	var xaxis = props.xaxis;
	var yaxis = props.yaxis;
	var data = rawData.filter(item => isValidPoint(item, xaxis, yaxis));
	var managed = props.managed;
	var bar = props.bar;
	var trend = props.trend;

	if (data.length < 2) return null;

	var fontSize = Style.text.verySmall;

	if(textSize === 1)
		fontSize = Style.text.small;
	else if (textSize === 2)
		fontSize = Style.text.standard;

	var deviceWidth = Dimensions.get("window").width;
	var containerStyle = [styles.container, responsiveStyles.container];

	var nativeProps = {};

	if (Platform.OS !== "web") {
		var chartWidth = deviceWidth / 1.7;
		if (!props.isATA)
			chartWidth = deviceWidth * 0.95;
		containerStyle.push({
			width: chartWidth,
		});

		if (deviceWidth <= 600)
			nativeProps.width = chartWidth;
		else
			nativeProps.width = chartWidth * 0.70;

	}

	var xAxisLabel = props.xAxisLabel;
	var yAxisLabel = props.yAxisLabel;

	var managedColors = {
		tickLabels: Style.colors.black,
		grid: Style.colors.semiLightGrey,
		axis: Style.colors.grey,
	};

	var atouchawayColors = {
		tickLabels: Style.colors.white,
		grid: Style.colors.semiDarkGrey,
		axis: Style.colors.semiLightGrey,
	};

	var colors = managed ? managedColors : atouchawayColors;

	var xAxisStyle = {
		tickLabels: {
			fill: colors.tickLabels,
			padding: 5,
			fontSize: fontSize,
		},
		ticks: {
			padding: 0,
		},
		axis: {
			stroke: colors.axis,
		},
		grid: {
			stroke: colors.grid,
		},
		axisLabel: {
			fill: colors.tickLabels,
		},
	};

	var yAxisStyle = {
		tickLabels: {
			fill: colors.tickLabels,
			padding: width < 450 ? 1 : 5,
			fontSize: fontSize,
		},
		ticks: xAxisStyle.ticks,
		axis: xAxisStyle.axis,
		grid: xAxisStyle.grid,
		axisLabel: {
			fill: xAxisStyle.axisLabel.fill,
			angle: -90,
		},
	};

	var chartPadding = {
		left: 45,
		bottom: 30,
		top: 0,
		right: 20,
	};

	var barStyle = {
		data: {
			fill: Style.colors.secondary,
		},
	};

	var lineStyle = {
		data: {
			stroke: Style.colors.secondary,
		},
	};

	var trendLineStyle = {
		data: {
			stroke: Style.colors.red,
		},
	};

	var trendedData = trend ? data.map(trendData).filter(p => isFiniteNumber(p.y)) : null;

	var trendLine = trend && trendedData && trendedData.length > 1 ? (
		<VictoryLine data={trendedData} style={trendLineStyle} x={xaxis} y={yaxis} interpolation="natural" />
	) : null;

	var plot_type = bar ? (
		<VictoryBar barRatio={0.7} data={data} style={barStyle} x={xaxis} y={yaxis} interpolation="natural" />
	) : (data?.length > 5 ?
		(<VictoryLine data={data} style={lineStyle} x={xaxis} y={yaxis} interpolation="natural" />) : null
	);

	// Force a full remount of the SVG subtree on every data change to avoid
	// the RNSVG + RCTLegacyViewManagerInteropComponentView incremental update
	// crash on iOS (insertReactSubview:atIndex: receiving nil).
	var chartKey = buildChartKey(data, trendedData, xaxis, yaxis, !!bar, !!trend);

	return (
		<View style={containerStyle}>
			<View style={styles.viewForScrolling} />
			<StyledText style={styles.textY} contrast={!managed} small black={managed}>{yAxisLabel}</StyledText>
			<VictoryChart key={chartKey} padding={chartPadding} domainPadding={30}  containerComponent={<VictoryContainer portalZIndex={0} />} {...nativeProps}>
				<VictoryAxis fixLabelOverlap style={yAxisStyle} dependentAxis />
				<VictoryAxis fixLabelOverlap style={xAxisStyle} tickLabelComponent={<VictoryLabel textAnchor="middle" />} />
				{plot_type}
				{trendLine}
			</VictoryChart>
			<StyledText style={styles.textX} contrast={!managed} centered small black={managed}>{xAxisLabel}</StyledText>
		</View>
	);
}

function buildChartKey(data, trendedData, xaxis, yaxis, hasBar, hasTrend) {
	var n = data.length;
	var trendN = trendedData ? trendedData.length : 0;
	var first = data[0] || {};
	var last = data[n - 1] || {};
	var fx = (xaxis && first[xaxis]) || first.x || "";
	var fy = (yaxis && first[yaxis]) || first.y || "";
	var lx = (xaxis && last[xaxis]) || last.x || "";
	var ly = (yaxis && last[yaxis]) || last.y || "";
	return n + "|" + trendN + "|" + (hasBar ? "b" : "l") + (hasTrend ? "t" : "") + "|" + fx + ":" + fy + "|" + lx + ":" + ly;
}

function trendData(dataPoint){
	var y = isFiniteNumber(dataPoint.y0)
		? Math.round((Number(dataPoint.y) + Number(dataPoint.y0)) / 2)
		: dataPoint.y;
	return {
		x: dataPoint.x,
		y: y,
	};
}

class VictoryGraphErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}
	static getDerivedStateFromError() {
		return { hasError: true };
	}
	componentDidCatch(error) {
		console.warn("VictoryGraph render failed:", error && error.message);
	}
	render() {
		if (this.state.hasError) return null;
		return this.props.children;
	}
}

export default function SafeVictoryGraph(props) {
	return (
		<VictoryGraphErrorBoundary>
			<VictoryGraph {...props} />
		</VictoryGraphErrorBoundary>
	);
}
